/**
 * GitHub Copilot format.
 *
 * hoocode's preferred Copilot home is the `.github/plugin/` marker directory —
 * the convention used by the real-world plugins indexed by
 * github/copilot-plugins (e.g. microsoft/work-iq) and the documented location
 * for GitHub-hosted marketplaces. The official Copilot CLI plugin reference
 * (docs.github.com copilot/reference/copilot-cli-reference/cli-plugin-reference,
 * "File locations"; verified 2026-07) accepts several locations for both files
 * (`.plugin/`, plugin root, `.github/plugin/`, `.claude-plugin/`), so this
 * adapter reads them all — `.github/plugin/` first, then root, then `.plugin/`,
 * then the legacy hoocode layout — and writes `.github/plugin/`.
 * (`.claude-plugin/` belongs to the Claude adapter, which wins precedence, so a
 * `.claude-plugin`-only directory correctly parses as a Claude plugin; Copilot
 * reads those natively anyway.) The capability tree matches the Claude layout:
 *
 *   skills            skills/<name>/SKILL.md            (or the manifest's `skills` path override)
 *   commands          .github/prompts/<name>.prompt.md  (Copilot's prompt-file convention — the
 *                                                        analog of a slash command; a manifest
 *                                                        `commands` path or `commands/` dir is read too)
 *   subagents         agents/<name>.agent.md            (Copilot's custom-agent convention — the
 *                                                        `.agent.md` suffix and a YAML-list `tools`
 *                                                        frontmatter are what Copilot recognizes; the
 *                                                        reader still accepts bare `.md` names)
 *   hooks             hooks.json or hooks/hooks.json    ({ description?, hooks: { Event: [...] } })
 *   MCP servers       .mcp.json                         ({ mcpServers } | { servers })
 *   canvases          extensions/<name>/extension.mjs   (or the manifest's `extensions` path override;
 *                                                        opened on demand by /canvas, never loaded)
 *
 * Plus the Agent Plugins vendor content namespace, which is how
 * `github/awesome-copilot` publishes its whole catalog — portable skills stay at
 * `skills/`, and Copilot-specific capabilities move under a reverse-DNS
 * directory:
 *
 *   subagents         com.github.copilot/agents/<name>.md
 *   canvases          com.github.copilot/extensions/<name>/extension.mjs
 *
 * Workspace-level (non-plugin) conventions, per the current GitHub Copilot
 * customization docs (docs.github.com — agent skills, custom agents, prompt
 * files; verified 2026-07):
 *
 *   skills            .github/skills/<name>/SKILL.md    (agentskills.io open standard)
 *   custom agents     .github/agents/<name>.agent.md    (frontmatter: name, description,
 *                                                        tools as a YAML list, model, ...)
 *   prompt files      .github/prompts/<name>.prompt.md  (VS Code / Copilot prompt files)
 *
 * Earlier hoocode releases authored a different Copilot mapping
 * (`.github/copilot-plugin.json` + `.github/prompts/*.prompt.md` +
 * `.github/chatmodes/*.chatmode.md`); the reader still accepts that layout as a
 * legacy fallback so previously authored plugins keep loading. Copilot
 * conventions move quickly; this file is the single place that encodes them,
 * so tracking an upstream change never reaches beyond this adapter.
 */

import * as fs from "node:fs";
import * as path from "node:path";
import type { NormalizedPlugin } from "../manifest.js";
import {
	authoredHooksToConfig,
	COPILOT_CONTENT_DIR,
	detectCanvasExtensions,
	detectUnsupportedSurfaces,
	dirIfExists,
	emitJson,
	emitMarkdown,
	normalizeHooks,
	normalizeMcp,
	parseAuthor,
	type RawManifest,
	readJson,
	resolveCapabilityDir,
	slug,
	toolsYamlList,
	unknownManifestFields,
} from "./shared.js";
import type { EmittedFile, PluginDraft, PluginFormatAdapter } from "./types.js";

const MARKER_DIR = ".github";
/**
 * Manifest probe order, matching the Copilot CLI reference exactly:
 * `.plugin/` → root → `.github/plugin/` → (`.claude-plugin/`, which belongs to
 * the Claude adapter), with hoocode's legacy `.github/copilot-plugin.json` last.
 *
 * The order is the vendor's, not ours: a plugin carrying more than one manifest
 * must resolve to the same one under hoocode as under Copilot CLI, or the two
 * read different metadata from the same directory.
 */
const MANIFEST_REL_PATHS = [
	path.join(".plugin", "plugin.json"),
	"plugin.json",
	path.join(MARKER_DIR, "plugin", "plugin.json"),
	path.join(MARKER_DIR, "copilot-plugin.json"),
] as const;
/**
 * Authored manifest location: root `plugin.json`, which is what the Copilot CLI
 * plugin-creating guide teaches and probe position #2. An earlier version emitted
 * `.github/plugin/plugin.json` while the comment here claimed root was canonical;
 * the comment was right. (`.github/plugin/` is still *read*, third in the probe
 * order above, since real marketplace-indexed plugins use it.)
 */
const emitManifestRelPath = "plugin.json";

/**
 * Copilot prompt-file home. `.github/prompts/<name>.prompt.md` is the current
 * Copilot/VS Code prompt-file convention, and hoocode maps a plugin's commands
 * to it (both the writer and this reader), so it is the authored location for
 * commands — not merely a legacy fallback.
 */
const PROMPTS_DIR = path.join(MARKER_DIR, "prompts");
// Legacy authored layout (read-only fallback).
const LEGACY_CHATMODES_DIR = path.join(MARKER_DIR, "chatmodes");
/**
 * Copilot's subagent home inside the vendor content namespace. This is the
 * layout `github/awesome-copilot` publishes: portable skills stay at `skills/`
 * and everything Copilot-specific is emitted under `com.github.copilot/`
 * (`eng/materialize-plugins.mjs`). Without it every agent in that catalog parses
 * as nothing.
 */
const CONTENT_AGENTS_DIR = path.join(COPILOT_CONTENT_DIR, "agents");
// Documented alternates, not legacy: the reference lists both locations for each.
const ALT_MCP_FILE = path.join(MARKER_DIR, "mcp.json");
const ALT_HOOKS_FILE = path.join("hooks", "hooks.json");
// hoocode's own older authored layout, still read so those plugins keep loading.
const LEGACY_HOOKS_FILE = path.join(MARKER_DIR, "hooks", "hooks.json");

/** Read a hooks JSON file (either `{ hooks: {...} }` or a bare event map). */
function readHooksFile(root: string, rel: string): NormalizedPlugin["hooks"] {
	const file = path.join(root, rel);
	if (!fs.existsSync(file)) return undefined;
	const raw = readJson<{ hooks?: Record<string, unknown> } | Record<string, unknown>>(file);
	if (!raw || typeof raw !== "object") return undefined;
	const config = ("hooks" in raw && raw.hooks ? raw.hooks : raw) as NormalizedPlugin["hooks"];
	return config && Object.keys(config).length > 0 ? config : undefined;
}

function manifestPathFor(root: string): string | undefined {
	for (const rel of MANIFEST_REL_PATHS) {
		if (readJson(path.join(root, rel)) != null) return path.join(root, rel);
	}
	return undefined;
}

/**
 * Every plugin-relative path this adapter reads, for the Tier 2 drift check
 * (§2.2). Assembled from the constants above rather than restated, so a path
 * added to the reader cannot be missing from the declaration.
 */
export function copilotReadPaths(): string[] {
	return [
		...MANIFEST_REL_PATHS,
		PROMPTS_DIR,
		LEGACY_CHATMODES_DIR,
		ALT_MCP_FILE,
		ALT_HOOKS_FILE,
		LEGACY_HOOKS_FILE,
		"skills",
		"agents",
		CONTENT_AGENTS_DIR,
		// Canvas extensions: the container, the namespaced container, and the entry
		// file that is the whole detection contract.
		"extensions",
		path.join(COPILOT_CONTENT_DIR, "extensions"),
		"extension.mjs",
		// The `<name>.agent.md` subagent convention, which the reference writes as
		// a suffix rather than a path.
		".agent.md",
		"hooks.json",
		".mcp.json",
	].map((p) => p.replace(/\\/g, "/"));
}

export const copilotFormat: PluginFormatAdapter = {
	id: "copilot",
	platform: "github",
	precedence: 2,
	label: "GitHub Copilot (.github)",
	// .github/plugin/ first (hoocode's preferred Copilot home and the documented
	// location for GitHub-hosted marketplaces), then the legacy .github/ spot,
	// then the Copilot CLI's other probe locations (root, .plugin/).
	marketplaceFiles: [
		path.join(MARKER_DIR, "plugin", "marketplace.json"),
		path.join(MARKER_DIR, "marketplace.json"),
		"marketplace.json",
		path.join(".plugin", "marketplace.json"),
	],

	workspace: {
		root: MARKER_DIR,
		// Copilot reads repo skills from .github/skills (also .claude/skills and
		// .agents/skills, which the sibling adapters cover).
		emitSkill: (s) => ({
			path: path.join(MARKER_DIR, "skills", slug(s.name), "SKILL.md"),
			content: emitMarkdown({ name: s.name, description: s.description }, s.body),
		}),
		// Custom agents take `tools` as a YAML list (not the Claude comma string).
		emitAgent: (a) => ({
			path: path.join(MARKER_DIR, "agents", `${slug(a.name)}.agent.md`),
			content: emitMarkdown(
				{ name: a.name, description: a.description, tools: toolsYamlList(a.tools), model: a.model },
				a.body,
			),
		}),
		// The closest Copilot equivalent of a slash command is a prompt file.
		emitCommand: (c) => ({
			path: path.join(MARKER_DIR, "prompts", `${slug(c.name)}.prompt.md`),
			content: emitMarkdown({ description: c.description }, c.body),
		}),
	},

	hasManifest(root: string): boolean {
		return manifestPathFor(root) !== undefined;
	},

	detectPlugin(root: string): boolean {
		return manifestPathFor(root) !== undefined;
	},

	parsePlugin(root: string): NormalizedPlugin | null {
		const manifestPath = manifestPathFor(root);
		if (!manifestPath) return null;
		const raw = readJson<RawManifest>(manifestPath);
		if (!raw) return null;

		const id = (raw.name ?? path.basename(root)).trim();
		if (!id) return null;

		return {
			id,
			version: raw.version,
			description: raw.description,
			author: parseAuthor(raw.author),
			root,
			manifestPath,
			format: "copilot",
			// Single-format view; the registry widens this to every format present.
			supportPlatform: ["github"],
			// Claude-mirror layout (manifest overrides honored), with the legacy
			// prompts/chatmodes locations as read-only fallbacks.
			skillsDir: resolveCapabilityDir(root, raw.skills, "skills"),
			// Commands map to Copilot prompt files at `.github/prompts/`; a manifest
			// `commands` override or a conventional `commands/` dir still wins if present.
			commandsDir: resolveCapabilityDir(root, raw.commands, "commands") ?? dirIfExists(root, PROMPTS_DIR),
			agentsDir:
				resolveCapabilityDir(root, raw.agents, "agents") ??
				dirIfExists(root, CONTENT_AGENTS_DIR) ??
				dirIfExists(root, LEGACY_CHATMODES_DIR),
			themesDir: resolveCapabilityDir(root, raw.themes, "themes"),
			// Manifest first, then the reference's two locations (root `hooks.json`
			// and `hooks/hooks.json`), then hoocode's legacy `.github/hooks/`.
			hooks:
				normalizeHooks(raw.hooks, root) ??
				readHooksFile(root, "hooks.json") ??
				readHooksFile(root, ALT_HOOKS_FILE) ??
				readHooksFile(root, LEGACY_HOOKS_FILE),
			mcpServers: normalizeMcp(raw.mcpServers, root) ?? normalizeMcp(undefined, root, ALT_MCP_FILE),
			// Providers are a native-only concept.
			providers: undefined,
			// Canvas extensions: `extensions/` (or the manifest's own path) and the
			// namespaced `com.github.copilot/extensions/`. Not loaded into the
			// session — `/canvas` opens them on demand — but resolved here so the
			// plugin can report what it brought.
			canvasExtensions: detectCanvasExtensions(root, id, raw.extensions),
			// `lspServers` is a real Copilot manifest key we do not model, and
			// `extensions` carries a vendor-metadata map alongside the canvas path
			// read above; both are carried here so an edit cannot drop them.
			unknownFields: unknownManifestFields(raw),
			unsupportedSurfaces: detectUnsupportedSurfaces(root),
		};
	},

	emit(draft: PluginDraft): EmittedFile[] {
		// Only the manifest is Copilot-specific — the canonical location is root
		// `plugin.json` (Copilot CLI spec) — and the capability tree mirrors the
		// Claude layout, so a plugin authored for both platforms is one tree with a
		// root manifest for Copilot and a .claude-plugin/ manifest for Claude.
		const files: EmittedFile[] = [];

		files.push({
			path: emitManifestRelPath,
			content: emitJson({
				...(draft.unknownFields ?? {}),
				name: draft.id,
				...(draft.version ? { version: draft.version } : {}),
				...(draft.description ? { description: draft.description } : {}),
				// Spec: `author` is an object with a required `name`.
				...(draft.author ? { author: { name: draft.author } } : {}),
			}),
		});

		for (const s of draft.skills ?? []) {
			files.push({
				path: path.join("skills", slug(s.name), "SKILL.md"),
				content: emitMarkdown({ name: s.name, description: s.description }, s.body),
			});
		}
		for (const c of draft.commands ?? []) {
			// Copilot's analog of a slash command is a prompt file:
			// `.github/prompts/<name>.prompt.md` (docs.github.com prompt files;
			// verified 2026-07) — same target as this adapter's workspace.emitCommand.
			files.push({
				path: path.join(PROMPTS_DIR, `${slug(c.name)}.prompt.md`),
				content: emitMarkdown({ description: c.description }, c.body),
			});
		}
		for (const a of draft.agents ?? []) {
			// Copilot recognizes custom agents by the `.agent.md` suffix, and takes
			// `tools` as a YAML list (not the Claude comma string) — matching this
			// adapter's workspace.emitAgent and the current GitHub Copilot docs
			// (docs.github.com custom agents / plugins-creating; verified 2026-07).
			files.push({
				path: path.join("agents", `${slug(a.name)}.agent.md`),
				content: emitMarkdown(
					{ name: a.name, description: a.description, tools: toolsYamlList(a.tools), model: a.model },
					a.body,
				),
			});
		}
		if (draft.mcpServers?.length) {
			files.push({
				path: ".mcp.json",
				content: emitJson({
					mcpServers: Object.fromEntries(
						draft.mcpServers.map((s) => [
							s.name,
							{ command: s.command, ...(s.args ? { args: s.args } : {}), ...(s.env ? { env: s.env } : {}) },
						]),
					),
				}),
			});
		}
		if (draft.hooks?.length) {
			files.push({
				path: path.join("hooks", "hooks.json"),
				content: emitJson({ hooks: authoredHooksToConfig(draft.hooks) }),
			});
		}
		return files;
	},
};
